/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2017, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 */
define([
	'./ServiceBase',
	'underscore'
], function (Class, _) {
	'use strict';

	// Declare the class
	const QueryService = Class.extend({

		init: function (options) {
			QueryService.inherited('init', this, arguments);
			this.dashboardApi = options.dashboardApi;
			this.ajaxSvc = this.dashboardApi.getGlassCoreSvc('.Ajax');
			this.logger = this.dashboardApi.getGlassCoreSvc('.Logger');
			this.queryParameters = null;
		},

		_querySpecString: function (queryOptions, module) {
			if (_.isNumber(queryOptions.dataRowLimit)) {
				queryOptions.querySpec.limit = queryOptions.dataRowLimit;
			}

			if (this.queryParameters) {
				queryOptions.querySpec.parameterValues = this.queryParameters;
			}

			var metadataSubsetIds = module && module.getMetadataSubsetIds();
			if (metadataSubsetIds && metadataSubsetIds.length > 0) {
				queryOptions.querySpec.queryHints = _.extend(queryOptions.querySpec.queryHints || {}, {
					metadataSubsetIDs: metadataSubsetIds
				});
			}
			return (this.isMockServer) ? '\\Q' + JSON.stringify(queryOptions.querySpec) + '\\E' : JSON.stringify(queryOptions.querySpec);
		},

		/**
		 * @public api to excute query against DSS.
		 *
		 * @param {Object} options.querySpec Mandatory: the query specification to be sent to DSS.
		 * @param {Object} options.sourceIdOrModule: The sourceId or module object which we're querying
		 * @param {Object} options.requestOptions: additional request options
		 *
		 * @returns a promise.
		 */
		runQuery: function (options) {
			return this._sendRequest(options, this.getRequestId(options.sender));
		},

		_sendRequest: function (options, requestId) {
			// reset the parameters for subsequent predict query
			if (!options.querySpec.parameterValues) {
				this.queryParameters = null;
			}

			return this._getModule(options.sourceIdOrModule)
				.then((module) => {
					const querySpec = this._querySpecString(options, module);
					const queryUserId = this.dashboardApi.getService('.UserProfile').userAccount.id;
					return this._getDataSourceModifiedTime(options.sourceIdOrModule)
						.then(modTime => {
							return module.queryData(
								querySpec,
								{
									senderId: options.senderId,
									lastModified: modTime,
									queryUserId,
									qfb: (options.requestOptions && options.requestOptions.qfb) || 'none',
									qfbMode: ( options && options.requestOptions.qfbMode ) || undefined,
									ipaMode: ( options && options.requestOptions.ipaMode ) || undefined,
									getAdditionalHeaders: options.requestOptions.getAdditionalHeaders
								});
						});
				}).then((response) => {
					const data = response ? response.data : null;
					const ETag = response ? response.getResponseHeader('ETag') : null;
					const requestTime = response ? response.getResponseHeader('x-ca-requesttime') : null;
					const zipiId = response ? response.getResponseHeader('zipi.id') : null;

					if (this.isActiveRequest(requestId, options.sender)) {
						return {
							data: data,
							requestTime: requestTime,
							ETag: ETag,
							zipiId: zipiId
						};
					} else {
						return Promise.reject(_.extend(new Error(), {
							reason: 'staleRequest'
						}));
					}
				})
				.catch((jqXHR) => {
					return Promise.reject(jqXHR);
				});
		},

		_getModule: function (sourceIdOrModule) {
			const isDataModule = typeof sourceIdOrModule.queryData === 'function' || typeof sourceIdOrModule.queryPredictData === 'function';
			if (isDataModule) {
				return Promise.resolve(sourceIdOrModule);
			}
			const dataSources = this.dashboardApi.getFeature('dataSources.deprecated');
			return dataSources.getModule(sourceIdOrModule).then(module => {
				if (!module) {
					const dataSets = this.dashboardApi.getFeature('DataSets.internal');
					module = dataSets.getModule(sourceIdOrModule);
				}
				if (!module) {
					return Promise.reject(new Error('Data source with ID ' + sourceIdOrModule + ' not found.'));
				}
				return module;
			});
		},

		_getDataSourceModifiedTime: function(sourceIdOrModule){
			let datasource = this.dashboardApi.getFeature('DataSources').getDataSource(sourceIdOrModule);
			if (!datasource) {
				const dataSets = this.dashboardApi.getFeature('DataSets.internal');
				datasource = dataSets.getDataSource(sourceIdOrModule);
				if (!datasource) {
					return Promise.reject(new Error('Datasource with source ID: ' + sourceIdOrModule + ' not found'));
				}
			}
			return datasource.getModificationTime();
		},

		/**
		 * Make the rest API call to query the map data for locations.
		 * @param locations Array of locations to be mapped
		 * @param useMapbox - flag to tell smarter maps to return mapbox style geojson
		 */
		runGeoQuery: function (payload, endpoint) {
			this.logger.debug('runGeoQuery');

			return this.ajaxSvc
				.ajax({
					url: endpoint || 'v1/geo/~',
					type: 'POST',
					headers: {
						'Content-Type': 'application/json'
					},
					data: JSON.stringify(payload),
					processData: false
				}).then(response => response.data);
		},

		/**
		 * Make the rest API call to perform a top or bottom query the module.
		 * @param querySpec The query specification to run
		 */
		runTopBottomQuery: function (queryOptions) {
			return this.runQuery(queryOptions);
		}
	});

	return QueryService;
});
